import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  ButtonBase,
  Chip,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import { DateTime } from "luxon";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import liff from "../../../lib/liff";

import ButtonDialog from "default/components/ButtonDialog";
import Loading from "default/components/Loading";
import { COLORS } from "constants/colors";
import {
  createQuestClearRequest,
  getQuestClearMessages,
  getQuestClearRequests,
  listQuests,
} from "utils/api";
import AppLayout from "works/chi/components/AppLayout";
import StyledButton from "works/chi/components/StyledButton";

const SITUATION_ADD_CLEAR = "add_clear";
const SITUATION_REMOVE_CLEAR = "remove_clear";

function QuestClearRequest() {
  const { t, i18n } = useTranslation(["chi"]);
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const soulId = queryParams.get("soul_id");
  const lng = queryParams.get("lng") || i18n.language || "ja";

  const [step, setStep] = useState(1);
  const [situation, setSituation] = useState(SITUATION_ADD_CLEAR);
  const [quests, setQuests] = useState([]);
  const [selectedQuest, setSelectedQuest] = useState(null);
  const [messageGroups, setMessageGroups] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [selectedMessageGroup, setSelectedMessageGroup] = useState(null);
  const [reasonText, setReasonText] = useState("");
  const [recentRequests, setRecentRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isError, setIsError] = useState(false);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [newlyLoadedMessageGroupId, setNewlyLoadedMessageGroupId] = useState(null);
  const messageGroupRefs = useRef({});

  const displayZone =
    lng === "en" ? "Europe/Warsaw" : lng === "th" ? "Asia/Bangkok" : "Asia/Tokyo";

  const formatDateTime = (isoString) => {
    const dt = DateTime.fromISO(isoString, { zone: "utc" })
      .setZone(displayZone)
      .setLocale(lng);
    return lng === "en"
      ? dt.toFormat("MMM d, yyyy HH:mm")
      : dt.toFormat("yyyy/MM/dd HH:mm");
  };

  const withLng = (path) => {
    const hasQuery = path.includes("?");
    return lng ? `${path}${hasQuery ? "&" : "?"}lng=${lng}` : path;
  };

  const loadRecentRequests = useCallback(async () => {
    if (!soulId) {
      return;
    }
    const data = await getQuestClearRequests(soulId, { limit: 10 });
    setRecentRequests(data.requests || []);
  }, [soulId]);

  const loadInitialData = useCallback(async () => {
    try {
      setIsError(false);
      setIsLoading(true);
      const [questList] = await Promise.all([listQuests(soulId), loadRecentRequests()]);
      setQuests(questList || []);
    } catch (error) {
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  }, [loadRecentRequests, soulId]);

  const loadMessages = useCallback(
    async ({ cursor = null, append = false } = {}) => {
      try {
        if (append) {
          setIsLoadingMore(true);
        } else {
          setIsLoadingMessages(true);
        }
        const data = await getQuestClearMessages(soulId, { cursor, limit: 30 });
        const loadedGroups = data.message_groups || [];
        setMessageGroups((prev) =>
          append ? [...prev, ...loadedGroups] : loadedGroups
        );
        if (append && loadedGroups.length > 0) {
          setNewlyLoadedMessageGroupId(loadedGroups[0].message_group_id);
        }
        setNextCursor(data.next_cursor || null);
        setHasMoreMessages(Boolean(data.has_more));
      } catch (error) {
        setIsError(true);
      } finally {
        setIsLoadingMessages(false);
        setIsLoadingMore(false);
      }
    },
    [soulId]
  );

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  useEffect(() => {
    if (step === 3 && messageGroups.length === 0 && !isLoadingMessages) {
      loadMessages();
    }
  }, [step, messageGroups.length, isLoadingMessages, loadMessages]);

  useEffect(() => {
    if (!newlyLoadedMessageGroupId || isLoadingMore) {
      return;
    }
    const element = messageGroupRefs.current[newlyLoadedMessageGroupId];
    element?.scrollIntoView?.({ block: "center", behavior: "smooth" });
    setNewlyLoadedMessageGroupId(null);
  }, [isLoadingMore, newlyLoadedMessageGroupId, messageGroups.length]);

  const canSubmit = useMemo(() => {
    return Boolean(
      situation &&
        selectedQuest &&
        selectedMessageGroup
    );
  }, [selectedMessageGroup, selectedQuest, situation]);

  const selectableQuests = useMemo(
    () => quests.filter((quest) => !quest.is_locked),
    [quests]
  );

  const handleSubmit = async () => {
    if (!canSubmit) {
      return;
    }
    try {
      setIsProcessing(true);
      await createQuestClearRequest(soulId, {
        questKey: selectedQuest.quest_key,
        situation,
        reasonText: reasonText.trim(),
        messageGroupId: selectedMessageGroup.message_group_id,
      });
      setOpenConfirm(false);
      setIsCompleted(true);
      await loadRecentRequests();
    } catch (error) {
      setOpenConfirm(false);
      setIsError(true);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleBackToMyPage = () => {
    navigate(withLng(`/chi/mypage?soul_id=${soulId}`));
  };

  const handleBackToChat = () => {
    liff.closeWindow();
  };

  const handleLoadOlderMessages = () => {
    if (!hasMoreMessages || isLoadingMore) {
      return;
    }
    loadMessages({ cursor: nextCursor, append: true });
  };

  const renderStepTitle = () => {
    if (isCompleted) {
      return t("chi:questClearRequest.completedTitle");
    }
    if (step === 1) {
      return t("chi:questClearRequest.situationTitle");
    }
    if (step === 2) {
      return t("chi:questClearRequest.questTitle");
    }
    if (step === 3) {
      return t("chi:questClearRequest.messageTitle");
    }
    return t("chi:questClearRequest.reasonTitle");
  };

  const renderStatusLabel = (quest) => {
    if (quest.is_cleared) {
      return t("chi:questClearRequest.statusCleared");
    }
    if (quest.is_locked) {
      return t("chi:questClearRequest.statusLocked");
    }
    return t("chi:questClearRequest.statusOpen");
  };

  const renderMessageGroup = (group) => {
    const selected = selectedMessageGroup?.message_group_id === group.message_group_id;
    const texts = group.texts && group.texts.length > 0 ? group.texts : [group.text];
    return (
      <Box
        key={group.message_group_id}
        ref={(element) => {
          messageGroupRefs.current[group.message_group_id] = element;
        }}
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 1.25,
        }}
      >
        <ButtonBase
          onClick={() => setSelectedMessageGroup(group)}
          sx={{
            width: "100%",
            display: "block",
            textAlign: "left",
            border: selected
              ? `2px solid ${COLORS.SKY_BLUE}`
              : "1px solid rgba(255,255,255,0.55)",
            borderRadius: "6px",
            px: 2,
            py: 1.5,
            background: selected ? "rgba(255,255,255,0.18)" : "rgba(0,0,0,0.28)",
            color: COLORS.TEXT_WHITE,
          }}
        >
          <Typography sx={{ fontSize: 12, lineHeight: 1.4, opacity: 0.82, mb: 1 }}>
            {formatDateTime(group.created_at)}
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
            {texts.map((text, index) => (
              <Box
                key={`${group.message_group_id}-${index}`}
                sx={{
                  alignSelf: "flex-start",
                  maxWidth: "100%",
                  borderRadius: "6px",
                  px: 1.5,
                  py: 1,
                  background: "rgba(255,255,255,0.12)",
                }}
              >
                <Typography
                  sx={{
                    fontSize: 15,
                    lineHeight: 1.7,
                    whiteSpace: "pre-line",
                    overflowWrap: "anywhere",
                  }}
                >
                  {text}
                </Typography>
              </Box>
            ))}
          </Box>
        </ButtonBase>
        {selected && (
          <StyledButton
            variant="contained"
            onClick={() => setStep(4)}
            sx={{ alignSelf: "center" }}
          >
            {t("chi:questClearRequest.next")}
          </StyledButton>
        )}
      </Box>
    );
  };

  const renderSituationStep = () => (
    <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 2 }}>
      <FormControl>
        <RadioGroup
          value={situation}
          onChange={(event) => setSituation(event.target.value)}
        >
          <FormControlLabel
            value={SITUATION_ADD_CLEAR}
            control={<Radio sx={{ color: COLORS.TEXT_WHITE }} />}
            label={t("chi:questClearRequest.situationAdd")}
            sx={{ color: COLORS.TEXT_WHITE, mb: 1 }}
          />
          <FormControlLabel
            value={SITUATION_REMOVE_CLEAR}
            control={<Radio sx={{ color: COLORS.TEXT_WHITE }} />}
            label={t("chi:questClearRequest.situationRemove")}
            sx={{ color: COLORS.TEXT_WHITE }}
          />
        </RadioGroup>
      </FormControl>
      <StyledButton
        variant="contained"
        disabled={!situation}
        onClick={() => setStep(2)}
        sx={{ alignSelf: "center", mt: 1 }}
      >
        {t("chi:questClearRequest.next")}
      </StyledButton>
    </Box>
  );

  const renderQuestStep = () => (
    <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 1.5 }}>
      {selectableQuests.map((quest) => {
        const selected = selectedQuest?.quest_key === quest.quest_key;
        return (
          <ButtonBase
            key={quest.quest_key}
            onClick={() => setSelectedQuest(quest)}
            sx={{
              width: "100%",
              display: "block",
              textAlign: "left",
              border: selected
                ? `2px solid ${COLORS.SKY_BLUE}`
                : "1px solid rgba(255,255,255,0.55)",
              borderRadius: "6px",
              px: 2,
              py: 1.5,
              background: selected ? "rgba(255,255,255,0.18)" : "rgba(0,0,0,0.28)",
              color: COLORS.TEXT_WHITE,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75 }}>
              <Typography sx={{ fontSize: 15, fontWeight: "bold", flex: 1 }}>
                {quest.name}
              </Typography>
              <Chip
                label={renderStatusLabel(quest)}
                size="small"
                sx={{
                  color: COLORS.TEXT_WHITE,
                  border: "1px solid rgba(255,255,255,0.45)",
                  background: "rgba(255,255,255,0.12)",
                  fontSize: 11,
                }}
              />
            </Box>
            <Typography sx={{ fontSize: 13, lineHeight: 1.6, opacity: 0.84 }}>
              {quest.description}
            </Typography>
          </ButtonBase>
        );
      })}
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5, mt: 1 }}>
        <StyledButton
          variant="contained"
          disabled={!selectedQuest}
          onClick={() => setStep(3)}
        >
          {t("chi:questClearRequest.next")}
        </StyledButton>
        <StyledButton variant="contained" onClick={() => setStep(1)}>
          {t("chi:questClearRequest.previous")}
        </StyledButton>
      </Box>
    </Box>
  );

  const renderMessageStep = () => (
    <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 2 }}>
      {isLoadingMessages && <Loading />}
      {!isLoadingMessages && messageGroups.length === 0 && (
        <Typography sx={{ color: COLORS.TEXT_WHITE, fontSize: 14, textAlign: "center" }}>
          {t("chi:questClearRequest.emptyMessages")}
        </Typography>
      )}
      {messageGroups.map(renderMessageGroup)}
      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 1.5,
          mt: 1,
        }}
      >
        {hasMoreMessages && (
          <StyledButton
            variant="contained"
            disabled={isLoadingMore}
            onClick={handleLoadOlderMessages}
          >
            {isLoadingMore
              ? t("chi:questClearRequest.loading")
              : t("chi:questClearRequest.loadOlder")}
          </StyledButton>
        )}
        <StyledButton variant="contained" onClick={() => setStep(2)}>
          {t("chi:questClearRequest.previous")}
        </StyledButton>
      </Box>
    </Box>
  );

  const renderReasonStep = () => (
    <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 2 }}>
      <TextField
        value={reasonText}
        onChange={(event) => setReasonText(event.target.value)}
        multiline
        minRows={5}
        placeholder={t("chi:questClearRequest.reasonPlaceholder")}
        fullWidth
        inputProps={{
          "aria-label": t("chi:questClearRequest.reasonTitle"),
          maxLength: 700,
        }}
        sx={{
          "& .MuiInputBase-root": {
            color: COLORS.TEXT_WHITE,
            background: "rgba(0,0,0,0.28)",
            borderRadius: "6px",
          },
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "rgba(255,255,255,0.55)",
          },
          "& .MuiInputBase-input::placeholder": {
            color: "rgba(255,255,255,0.58)",
            opacity: 1,
          },
        }}
      />
      <StyledButton
        variant="contained"
        disabled={!canSubmit}
        onClick={() => setOpenConfirm(true)}
        sx={{ alignSelf: "center", mt: 1 }}
      >
        {reasonText.trim().length > 0
          ? t("chi:questClearRequest.confirm")
          : t("chi:questClearRequest.skipReason")}
      </StyledButton>
      <StyledButton variant="contained" onClick={() => setStep(3)} sx={{ alignSelf: "center" }}>
        {t("chi:questClearRequest.previous")}
      </StyledButton>
    </Box>
  );

  const renderCompletedStep = () => (
    <Typography
      sx={{
        color: COLORS.TEXT_WHITE,
        fontSize: 15,
        lineHeight: 1.8,
        textAlign: "center",
        whiteSpace: "pre-line",
      }}
    >
      {t("chi:questClearRequest.completedDescription")}
    </Typography>
  );

  const renderRecentRequests = () => {
    if (recentRequests.length === 0) {
      return null;
    }
    return (
      <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 1.25, mt: 2 }}>
        <Typography
          component="h2"
          sx={{
            color: COLORS.TEXT_WHITE,
            fontFamily: '"Noto Serif JP", serif',
            fontSize: 17,
            fontWeight: "bold",
            textAlign: "center",
          }}
        >
          {t("chi:questClearRequest.recentTitle")}
        </Typography>
        {recentRequests.map((request) => (
          <Box
            key={request.request_id}
            sx={{
              border: "1px solid rgba(255,255,255,0.45)",
              borderRadius: "6px",
              px: 2,
              py: 1.5,
              background: "rgba(0,0,0,0.24)",
              color: COLORS.TEXT_WHITE,
            }}
          >
            <Typography sx={{ fontSize: 14, fontWeight: "bold", mb: 0.5 }}>
              {request.quest_name}
            </Typography>
            <Typography sx={{ fontSize: 12, lineHeight: 1.6, opacity: 0.86 }}>
              {formatDateTime(request.created_at)} /{" "}
              {t(`chi:questClearRequest.requestStatus.${request.status}`)}
            </Typography>
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <AppLayout>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          minHeight: "100vh",
          py: 4,
          gap: 3,
        }}
      >
        <Typography
          component="h1"
          sx={{
            color: COLORS.TEXT_WHITE,
            fontFamily: '"Noto Serif JP", serif',
            fontSize: 24,
            fontWeight: "bold",
            textAlign: "center",
          }}
        >
          {t("chi:questClearRequest.title")}
        </Typography>

        {isLoading && <Loading />}

        {!isLoading && (
          <>
            <Typography
              component="h2"
              sx={{
                color: COLORS.TEXT_WHITE,
                fontFamily: '"Noto Serif JP", serif',
                fontSize: 17,
                fontWeight: "bold",
                lineHeight: 1.7,
                textAlign: "center",
                whiteSpace: "pre-line",
              }}
            >
              {renderStepTitle()}
            </Typography>

            {isError && (
              <Typography
                sx={{
                  color: COLORS.TEXT_WHITE,
                  fontSize: 14,
                  lineHeight: 1.7,
                  textAlign: "center",
                  whiteSpace: "pre-line",
                }}
              >
                {t("chi:questClearRequest.error")}
              </Typography>
            )}

            {isCompleted && renderCompletedStep()}
            {!isCompleted && step === 1 && renderSituationStep()}
            {!isCompleted && step === 2 && renderQuestStep()}
            {!isCompleted && step === 3 && renderMessageStep()}
            {!isCompleted && step === 4 && renderReasonStep()}

            {renderRecentRequests()}

            <Box sx={{ width: "100%", display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5, mt: 1 }}>
              <StyledButton variant="contained" onClick={handleBackToMyPage}>
                {t("chi:questClearRequest.back")}
              </StyledButton>
              <StyledButton variant="contained" onClick={handleBackToChat}>
                {t("chi:questClearRequest.backToChat")}
              </StyledButton>
            </Box>
          </>
        )}
      </Box>

      <ButtonDialog
        open={openConfirm}
        title={t("chi:questClearRequest.confirmTitle")}
        description={t("chi:questClearRequest.confirmDescription")}
        buttons={[
          {
            label: t("chi:questClearRequest.submit"),
            onClick: handleSubmit,
            disabled: isProcessing,
          },
        ]}
        handleClose={() => setOpenConfirm(false)}
      />
    </AppLayout>
  );
}

export default QuestClearRequest;
